<template>
    <div class="row justify-content-center">
        <div class="col-12 border bg-white pt-3" style="max-width: 400px;" :class="{ 'col-md-8 col-lg-6': !block }">
            <div v-show="!isFullyInitialized" class="text-center p-4">
                <b-spinner variant="primary" label="Loading..."></b-spinner>
                <div class="mt-2">Loading payment form...</div>
            </div>
            
            <div v-show="isFullyInitialized">
                <!-- Payment Request Button -->
                <div v-if="paymentRequestAvailable" class="mb-3 text-center">
                    <div id="payment-request-button"></div>
                </div>

                <!-- 'or' separator -->
                <div v-if="paymentRequestAvailable" class="or-separator">
                    <div class="line"></div>
                    <div class="or-text">or</div>
                    <div class="line"></div>
                </div>

                <!-- Card Form -->
                <div>
                    <div class="card-form-container">
                        <b-alert variant="danger" :show="showError">{{ error }}</b-alert>
                        <b-alert variant="danger" :show="showGenericError">
                            Sorry, an error occurred with your payment. No charge was taken. Please try again or contact support.
                            {{ genericErrorMsg }}
                        </b-alert>

                        <div id="card-element" class="card-element-border"></div>

                        <!-- Used to display form errors. -->
                        <div id="card-errors" role="alert"></div>

                        <div class="text-center mt-3 mb-3">
                            <button class="btn btn-primary btn-sm" @click="handleFormSubmit" :disabled="isBusy">Save Payment Method</button>
                        </div>
                        <div class="text-center mb-3" v-if="withCancel">
                            <button class="btn btn-sm btn-secondary mt-3" @click="cancel">Cancel</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        user: {
            type: Object
        },
        redirect: {
            type: String,
            required: false,
        },
        isInline: {
            type: Boolean,
            required: false,
        },
        withCancel: {
            type: Boolean,
            required: false,
            default: false,
        },
        block: {
            type: Boolean,
            required: false,
            default: false,
        },
        amount: {
            type: Number,
            required: false,
            default: 0,
        }
    },

    data() {
        return {
            stripeAPIToken: null,
            stripe: null,
            elements: null,
            card: null,
            showGenericError: false,
            genericErrorMsg: '',
            error: '',
            showError: false,
            isBusy: false,
            userData: {},
            paymentRequestAvailable: false,
            setupIntentClientSecret: '',
            paymentRequest: null,
            isStripeLoaded: false,
            isInitialized: false,
            isCardReady: false,
            isPaymentRequestReady: false
        }
    },

    computed: {
        isFullyInitialized() {
            // If payment request is available, wait for both card and payment request
            if (this.paymentRequestAvailable) {
                return this.isInitialized && this.isCardReady && this.isPaymentRequestReady;
            }
            // If no payment request, only wait for card
            return this.isInitialized && this.isCardReady;
        }
    },

    mounted() {
        try {
            const stripeKeyMeta = document.querySelector('meta[name="stripe-key"]');
            if (!stripeKeyMeta) {
                throw new Error('Stripe API key meta tag not found');
            }
            this.stripeAPIToken = stripeKeyMeta.getAttribute('content');
            
            this.includeStripe('js.stripe.com/v3/', function (error) {
                if (error) {
                    console.error('Failed to load Stripe:', error);
                    this.showGenericError = true;
                    this.genericErrorMsg = 'Failed to initialize payment system. Please refresh the page or try again later.';
                    return;
                }
                // Wait for Stripe to be definitely available
                this.waitForStripe()
                    .then(() => this.configureStripe())
                    .catch(error => {
                        console.error('Error waiting for Stripe:', error);
                        this.showGenericError = true;
                        this.genericErrorMsg = 'Failed to initialize payment system. Please refresh the page or try again later.';
                    });
            }.bind(this));

            if (this.user) {
                this.userData = this.user;
            } else {
                this.userData = window.virtuoso.user;
            }
        } catch (error) {
            console.error('Error initializing component:', error);
            this.showGenericError = true;
            this.genericErrorMsg = 'Failed to initialize payment system. Please refresh the page or try again later.';
        }
    },

    methods: {
        includeStripe(URL, callback) {
            let documentTag = document, tag = 'script',
                object = documentTag.createElement(tag),
                scriptTag = documentTag.getElementsByTagName(tag)[0];
            object.src = '//' + URL;
            if (callback) {
                object.addEventListener('load', function (e) {
                    callback(null, e);
                }, false);
            }
            scriptTag.parentNode.insertBefore(object, scriptTag);
        },

        waitForStripe(attempts = 0) {
            return new Promise((resolve, reject) => {
                if (window.Stripe) {
                    resolve();
                    return;
                }
                
                if (attempts > 10) {
                    reject(new Error('Stripe failed to load'));
                    return;
                }

                setTimeout(() => {
                    this.waitForStripe(attempts + 1)
                        .then(resolve)
                        .catch(reject);
                }, 100);
            });
        },

        configureStripe() {
            try {
                this.stripe = Stripe(this.stripeAPIToken);
                this.elements = this.stripe.elements();

                // First get the Setup Intent
                return axios.post('/api/billing/create-setup-intent')
                    .then(response => {
                        this.setupIntentClientSecret = response.data.client_secret;
                        
                        // Set initialized first so the DOM elements are rendered
                        this.isInitialized = true;
                        
                        // Wait for DOM to update before mounting elements
                        this.$nextTick(() => {
                            // Check if elements exist after DOM update
                            const cardElement = document.getElementById('card-element');
                            if (!cardElement) {
                                console.error('Card element not found in DOM after initialization');
                                this.showGenericError = true;
                                this.genericErrorMsg = 'Failed to initialize payment form. Please refresh the page.';
                                return;
                            }

                            // Initialize elements only when DOM is ready
                            this.initializeCard();
                            this.initializePaymentRequest();
                        });
                    })
                    .catch(error => {
                        console.error('Error creating SetupIntent:', error);
                        this.showGenericError = true;
                        this.genericErrorMsg = 'Failed to initialize payment system. Please refresh the page or try again later.';
                        this.isInitialized = true;
                    });
            } catch (error) {
                console.error('Error in configureStripe:', error);
                this.showGenericError = true;
                this.genericErrorMsg = 'Failed to initialize payment system. Please refresh the page or try again later.';
                this.isInitialized = true;
            }
        },

        initializeCard() {
            // Create the card element
            this.card = this.elements.create('card');
            
            // Set up event handlers before mounting
            this.card.on('ready', () => {
                console.log('Card element is ready');
                this.isCardReady = true;
            });

            // Handle real-time validation errors from the card Element.
            this.card.on('change', (event) => {
                if (event.error) {
                    this.error = event.error.message;
                    this.showError = true;
                } else {
                    this.error = '';
                    this.showError = false;
                }
            });

            // Mount the card element
            this.card.mount('#card-element');
        },

        initializePaymentRequest() {
            // Create a PaymentRequest object
            this.paymentRequest = this.stripe.paymentRequest({
                country: 'GB',
                currency: 'gbp',
                total: {
                    label: 'Add payment method',
                    amount: this.amount,
                },
                requestPayerName: true,
                requestPayerEmail: true,
            });

            // Check availability of Payment Request API
            this.paymentRequest.canMakePayment().then(result => {
                if (result) {
                    this.paymentRequestAvailable = true;

                    // Wait for the DOM to update before mounting
                    this.$nextTick(() => {
                        // Create and mount the Payment Request Button
                        const prButton = this.elements.create('paymentRequestButton', {
                            paymentRequest: this.paymentRequest,
                            style: {
                                paymentRequestButton: {
                                    type: 'default',
                                    theme: 'dark',
                                    height: '44px',
                                },
                            },
                        });

                        const prElement = document.getElementById('payment-request-button');
                        if (prElement) {
                            prButton.mount('#payment-request-button');
                            // Mark payment request as ready once mounted
                            this.isPaymentRequestReady = true;
                        }

                        // Handle the paymentmethod event
                        this.paymentRequest.on('paymentmethod', this.handlePaymentMethod);
                    });
                } else {
                    this.paymentRequestAvailable = false;
                    // If payment request is not available, mark it as ready
                    this.isPaymentRequestReady = true;
                }
            });
        },

        handlePaymentMethod(ev) {
            // First call confirmCardSetup with handleActions: false so that
            // no 3DS modal is immediately shown.
            this.stripe.confirmCardSetup(
                this.setupIntentClientSecret,
                { payment_method: ev.paymentMethod.id },
                { handleActions: false }
            ).then(result => {
                if (result.error) {
                    // Inform the Payment Request that the payment failed
                    ev.complete('fail');
                    this.showGenericError = true;
                    this.genericErrorMsg = result.error.message;
                    this.isBusy = false;
                    return;
                }

                // Close the Payment Request modal
                ev.complete('success');

                // Check if additional authentication (3DS) is required
                if (result.setupIntent.status === 'requires_action') {
                    // Wait a short time to ensure the Payment Request modal is closed
                    setTimeout(() => {
                        // Now call confirmCardSetup again without the handleActions flag,
                        // so that Stripe will display the 3DS modal for authentication.
                        this.stripe.confirmCardSetup(this.setupIntentClientSecret, {
                            payment_method: ev.paymentMethod.id,
                        }).then(finalResult => {
                            if (finalResult.error) {
                                this.showGenericError = true;
                                this.genericErrorMsg = finalResult.error.message;
                                this.isBusy = false;
                            } else {
                                // Successfully handled authentication; process the payment method.
                                this.stripePaymentMethodHandler({ paymentMethod: ev.paymentMethod });
                            }
                        });
                    }, 300); // 300ms delay; adjust as needed
                } else {
                    // No additional authentication needed.
                    this.stripePaymentMethodHandler({ paymentMethod: ev.paymentMethod });
                }
            });
        },

        /**
         * Handle payment method
         */
        stripePaymentMethodHandler(result) {
            if (result.error) {
                // Show error in payment form
                this.showGenericError = true;
                this.genericErrorMsg = result.error.message;
                this.isBusy = false;
            } else {
                // Send paymentMethod.id to your server
                axios.post('/api/billing/upsert-customer', {
                    user_id: this.userData.id,
                    payment_method: result.paymentMethod
                }).then((result) => {
                    return result;
                }).then((customer) => {
                    // The customer has been created
                    if (customer.data.error) {
                        this.error = customer.data.error;
                        this.showError = true;
                        this.isBusy = false;
                    } else {
                        if (this.redirect) {
                            location.href = this.redirect;
                        } else {
                            if (this.isInline) {
                                // Call a bus event
                                Bus.$emit('updatedPaymentMethods', true);
                                console.log('Emitted updatedPaymentMethods');
                            } else {
                                location.href = '/account/update-payment-method/success';
                            }
                        }
                    }
                });
            }
        },

        handleFormSubmit() {
            this.showError = false;
            this.isBusy = true;

            this.stripe.createPaymentMethod({
                type: 'card',
                card: this.card,
                billing_details: {
                    name: this.userData.name,
                    email: this.userData.email,
                },
            }).then(this.stripePaymentMethodHandler);
        },

        /**
         * Send a cancel event
         */
        cancel() {
            Bus.$emit('cancelPaymentMethod', true);
        }
    }
}
</script>

<style>
/* Center the row content */
.row.justify-content-center {
    justify-content: center;
}

/* 'or' separator styles */
.or-separator {
    display: flex;
    align-items: center;
    text-align: center;
    margin: 20px 0;
}

.or-separator .line {
    flex: 1;
    height: 1px;
    background-color: #eee;
    border: none;
}

.or-separator .or-text {
    margin: 0 15px;
    color: #888;
}

/* Add border around card element */
.card-element-border {
    padding: 15px;
    border: 1px solid #ccc;
    border-radius: 4px;
    background-color: #fff;
    margin-bottom: 15px;
}

/* Center elements */
#payment-request-button {
    margin: 0 auto;
    padding: 0;
}

/* Center and limit form width */
#payment-form {
    max-width: 400px;
    margin: 0 auto;
}

/* Adjust Stripe Element styles */
.StripeElement {
    box-sizing: border-box;

    height: 40px;
    width: 100%;

    padding: 10px 12px;

    border-radius: 4px;
    background-color: transparent;

    -webkit-transition: box-shadow 150ms ease;
    transition: box-shadow 150ms ease;
}

.StripeElement--focus {
    box-shadow: none;
}

.StripeElement--invalid {
    border-color: #fa755a;
}

.StripeElement--webkit-autofill {
    background-color: #fefde5 !important;
}
</style>
